import { useSelector } from "react-redux";
import DataTableBase from "../DataTableBase";
import { Box, Button, Chip, Stack, Typography, alpha } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useState, useMemo, useCallback } from "react";
import { formatDate } from "../../utils/formatters";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import LabelDeleteDialog from "../Dialogs/LabelDelete";
import LabelEditDialog from "../Dialogs/labelsEdit";
import useLabels, { labelKeys } from "../../hooks/useLabels";

export default function LabelsTable() {
  const navigate = useNavigate();
  const selectedOrganisation = useSelector(
    (state) => state.selectedOrganisation
  );
  const { data: labels = [] } = useLabels(selectedOrganisation?.id);
  const queryClient = useQueryClient();
  const { t } = useTranslation("common");

  const [deleteLabelConfirmationOpen, setDeleteLabelConfirmationOpen] =
    useState(false);

  const [editLabelDialogOpen, setEditLabelDialogOpen] = useState(false);
  const [label, setLabel] = useState({});

  const labelsById = useMemo(() => {
    const map = new Map();
    labels.forEach((item) => {
      map.set(Number(item.id), item);
    });
    return map;
  }, [labels]);

  const handleNavigateToLabel = useCallback(
    (id) => {
      navigate(`/label/${id}`);
    },
    [navigate]
  );

  const handleOpenEdit = useCallback(
    (id) => {
      setLabel(labelsById.get(Number(id)) || {});
      setEditLabelDialogOpen(true);
    },
    [labelsById]
  );

  const handleOpenDelete = useCallback(
    (id) => {
      setDeleteLabelConfirmationOpen(true);
      setLabel(labelsById.get(Number(id)) || {});
    },
    [labelsById]
  );

  const refreshLabel = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: labelKeys.lists(),
      }),
    [queryClient]
  );

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteLabelConfirmationOpen(false);
    refreshLabel();
  }, [refreshLabel]);

  const handleCloseEditDialog = useCallback(() => {
    setEditLabelDialogOpen(false);
  }, []);

  //columns for devices for data table
  const columns = useMemo(
    () => [
      {
        name: "id",
        label: t("common.ID"),
        options: {
          display: false,
        },
      },
      {
        name: "name",
        options: {
          label: t("common.name"),
          customBodyRender: (value, tableMeta) => {
            const rowColor = tableMeta.rowData[2];
            return (
              <Chip
                label={value}
                onClick={() => handleNavigateToLabel(tableMeta.rowData[0])}
                clickable
                sx={{
                  backgroundColor: rowColor ? alpha(rowColor, 0.15) : undefined,
                  color: rowColor || "inherit",
                  fontWeight: 600,
                  borderRadius: "16px",
                  cursor: "pointer",
                }}
              />
            );
          },
        },
      },
      {
        name: "color",
        label: t("labels.color"),
        options: {
          display: false,
        },
      },
      // time_updated
      {
        name: "time_updated",
        label: t("common.time_updated"),
        options: {
          display: false,
        },
      },
      // time_created
      {
        name: "time_created",
        label: t("common.time_updated"),
        options: {
          customBodyRender: (value, tableMeta) => {
            //if time_upated is not null, use that otherwise use time_created
            const timeUpdated = tableMeta.rowData[3];
            const displayValue = timeUpdated || value;

            return (
              <Typography variant="body2">
                {formatDate(displayValue)}
              </Typography>
            );
          },
        },
      },
      //options
      {
        name: "options",
        label: "Options",
        options: {
          customBodyRender: (value, tableMeta) => {
            return (
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => handleOpenEdit(tableMeta.rowData[0])}
                >
                  Edit
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => handleOpenDelete(tableMeta.rowData[0])}
                >
                  Delete
                </Button>
              </Stack>
            );
          },
        },
      },
    ],
    [t, handleNavigateToLabel, handleOpenEdit, handleOpenDelete]
  );

  return (
    <Box sx={{ mt: 1 }}>
      <DataTableBase
        title={t("labels.index")}
        data={labels}
        columns={columns}
      />
      <LabelDeleteDialog
        open={deleteLabelConfirmationOpen}
        handleClose={handleCloseDeleteDialog}
        refreshLabel={refreshLabel}
        label={label}
      />
      <LabelEditDialog
        open={editLabelDialogOpen}
        handleClose={handleCloseEditDialog}
        label={label}
      />
    </Box>
  );
}
